import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, MessageCircle, ThumbsUp, Send } from 'lucide-react';
import { forumAPI } from '../services/api';

const ForumPost = () => {
  const { postId } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  useEffect(() => { fetchPost(); }, [postId]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await forumAPI.getPost(postId);
      setPost(res.data?.post || res.data);
      setReplies(res.data?.replies || []);
    } catch {}
    setLoading(false);
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setReplyLoading(true);
    try {
      await forumAPI.replyToPost(postId, { content: replyContent });
      setReplyContent('');
      fetchPost();
    } catch {}
    setReplyLoading(false);
  };

  const handleLike = async () => {
    try {
      await forumAPI.likePost(postId);
      fetchPost();
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8"><div className="h-64 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" /></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-[#F8FAFC] text-2xl font-heading mb-4">Post Not Found</h2>
          <Link to="/forum"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black">Back to Forums</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/forum" className="flex items-center gap-2 text-[#64748B] hover:text-[#F59E0B] transition-colors mb-6" data-testid="back-to-forum">
          <ArrowLeft className="w-4 h-4" /> Back to Forums
        </Link>

        <article className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6 mb-6" data-testid="forum-post-detail">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[#64748B] text-xs bg-[#1E293B] px-2 py-0.5 rounded">{post.category_name || 'General'}</span>
          </div>
          <h1 className="text-[#F8FAFC] text-2xl font-bold mb-4 font-heading" data-testid="post-title">{post.title}</h1>
          <div className="flex items-center gap-4 mb-6 text-[#64748B] text-sm">
            <span>by <Link to={`/profile/${post.username}`} className="text-[#F59E0B] hover:underline">{post.username}</Link></span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{post.views || 0} views</span>
            <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{replies.length} replies</span>
          </div>
          <div className="text-[#94A3B8] leading-relaxed whitespace-pre-wrap mb-6">{post.content}</div>
          <div className="flex items-center gap-4 border-t border-[#1E293B] pt-4">
            <button onClick={handleLike} className="flex items-center gap-2 text-[#64748B] hover:text-[#F59E0B] transition-colors" data-testid="like-post-btn">
              <ThumbsUp className="w-4 h-4" /> <span className="text-sm">{post.likes || 0} likes</span>
            </button>
          </div>
        </article>

        <div className="mb-6">
          <h3 className="text-[#F8FAFC] font-bold text-lg mb-4 font-heading">Replies ({replies.length})</h3>
          {replies.length > 0 ? (
            <div className="space-y-3">
              {replies.map((r, i) => (
                <div key={r.id || i} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-5" data-testid={`reply-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-[#1E293B] flex items-center justify-center">
                      <span className="text-[#F59E0B] text-xs font-bold">{r.username?.slice(0,2).toUpperCase()}</span>
                    </div>
                    <span className="text-[#F8FAFC] font-medium text-sm">{r.username}</span>
                  </div>
                  <p className="text-[#94A3B8] text-sm whitespace-pre-wrap">{r.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-8 text-center">
              <p className="text-[#64748B]">No replies yet</p>
            </div>
          )}
        </div>

        {user ? (
          <form onSubmit={handleReply} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6" data-testid="reply-form">
            <h3 className="text-[#F8FAFC] font-bold mb-4 font-heading">Write a Reply</h3>
            <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} required rows={3}
              className="w-full bg-[#0A0E17] border border-[#1E293B] text-[#F8FAFC] rounded-md px-3 py-2 text-sm resize-none mb-4" placeholder="Share your thoughts..." data-testid="reply-content-input" />
            <Button type="submit" disabled={replyLoading} className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="submit-reply-btn">
              <Send className="w-4 h-4 mr-2" /> {replyLoading ? 'Posting...' : 'Reply'}
            </Button>
          </form>
        ) : (
          <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-6 text-center">
            <p className="text-[#64748B] mb-3">Login to reply</p>
            <Link to="/login"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold">Login</Button></Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ForumPost;
